import { Decorative } from "@faire/design-tokens";
import { MobileSlider } from "@faire/retailer-visitor-shared/components/MobileSlider/index";
import { TrustPilotWidget } from "@faire/retailer-visitor-shared/components/TestimonialCarousel/TrustPilotWidget";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { getTrustpilotRating } from "@faire/retailer-visitor-shared/serialized-data/getTrustpilotRating";
import {
  BASE_TESTIMONIAL_EU,
  BASE_TESTIMONIAL_NAM,
  ITestimonialData,
  TESTIMONIAL_LANGUAGE_GROUP,
  TRUST_PILOT_MINIMUM_RATING,
} from "@faire/retailer-visitor-shared/views/brands/landing-page/const";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column, Row, HorizontalLine } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { trackBrandLeadsBrandsPageMobileTestimonialView } from "@faire/web-api--source/events/brandLeads/view/brandsPageMobileTestimonial";
import { trackBrandLeadsTrustPilotBadgeView } from "@faire/web-api--source/events/brandLeads/view/trustPilotBadge";
import brandUrl from "@faire/web-api--source/routes/www/brand/brandToken";
import React, { useCallback, useEffect } from "react";
import styled from "styled-components";

export const BrandsLandingPageTestimonialsSwipe: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { retailerCountry, isEurope: isShowingCaseStudies } = useNationality();

  const country = retailerCountry || "";

  const countryAvailable = Object.keys(
    TESTIMONIAL_LANGUAGE_GROUP(strictLocalize)
  ).includes(country);

  const testimonials = useCallback(
    (strictLocalize: StrictLocalizeFunction): ITestimonialData[] =>
      (() => {
        if (countryAvailable) {
          // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
          return TESTIMONIAL_LANGUAGE_GROUP(strictLocalize)[country];
        } else if (isShowingCaseStudies) {
          return BASE_TESTIMONIAL_EU(strictLocalize);
        }
        return BASE_TESTIMONIAL_NAM(strictLocalize);
      })(),
    [country, countryAvailable, isShowingCaseStudies]
  );

  useEffect(() => {
    const testimonial = testimonials(strictLocalize)[0];
    if (testimonial) {
      trackBrandLeadsBrandsPageMobileTestimonialView(testimonial.brandToken);
    }
  }, [testimonials, strictLocalize]);

  const handleSliderChangeHandler = (_: number, newIndex: number) => {
    const testimonial = testimonials(strictLocalize)[newIndex];
    if (testimonial) {
      trackBrandLeadsBrandsPageMobileTestimonialView(testimonial.brandToken);
    }
  };

  const viewRef = useDoActionWhenVisible(trackBrandLeadsTrustPilotBadgeView);
  const FAIRE_TRUSTPILOT_RATING = getTrustpilotRating();

  return (
    <SwipeContainer>
      {!isShowingCaseStudies ? (
        <Column
          style={{
            padding: getSpacing("3x"),
          }}
        >
          <Typography variant="pageHeaderSerifRegular">
            <StrictLocalMsg defaultMessage="How brands have grown their businesses on Faire" />
          </Typography>
        </Column>
      ) : (
        <Spacer height="3x" />
      )}
      <MobileSlider
        numberOfSlides={testimonials.length}
        centerMode
        arrows={false}
        centerPadding="24px"
        infinite
        beforeChange={handleSliderChangeHandler}
        slidesToShow={1}
        slidesToScroll={1}
      >
        {testimonials(strictLocalize).map((testimonial, index) => (
          <div key={`${testimonial.brandToken}_${index}`}>
            <Testimonial testimonial={testimonial} />
          </div>
        ))}
      </MobileSlider>
      {isShowingCaseStudies &&
      FAIRE_TRUSTPILOT_RATING >= TRUST_PILOT_MINIMUM_RATING ? (
        <Column
          ref={viewRef}
          align="center"
          style={{ padding: getSpacing(0, "3x") }}
        >
          <HorizontalLine color={Decorative.tint.green} />
          <Spacer height="3x" />
          <TrustPilotWidget rating={FAIRE_TRUSTPILOT_RATING} />
          <Spacer height="4x" />
        </Column>
      ) : null}
    </SwipeContainer>
  );
};

const Testimonial = ({ testimonial }: { testimonial: ITestimonialData }) => {
  const isSSR = useIsSSR();
  return (
    <SwipeCardContainer>
      <SwipeCard>
        <TypographyScroll variant="paragraphSansMedium">
          {testimonial.content}
        </TypographyScroll>
        <Spacer size="1x" />
        <Row>
          <img
            style={{ width: "39px", height: "39px", borderRadius: "100%" }}
            src={optimizeImageUrl({ url: testimonial.brandImageUrl }, isSSR)}
          />
          <Spacer size="1x" />
          <Column>
            <Typography variant="paragraphSansMedium">
              <Link to={brandUrl(testimonial.brandToken)}>
                {testimonial.ownerName}, {testimonial.brandName}
              </Link>
            </Typography>
            <Typography variant="labelSansRegular">
              {testimonial.ownerCity ? testimonial.ownerCity + ", " : ""}
              {testimonial.brandRegion}
            </Typography>
          </Column>
        </Row>
      </SwipeCard>
    </SwipeCardContainer>
  );
};

const SwipeContainer = styled(Column)`
  background-color: ${Decorative.tint.neutral};
  width: 100%;
  height: fit-content;
`;

const SwipeCardContainer = styled(Row)`
  background-color: transparent;
  padding: 4px;
`;

const TypographyScroll = styled(Typography)`
  overflow-y: auto;
`;

const SwipeCard = styled(Column).attrs({ justify: "center" })`
  padding: ${getSpacing("3x")};
  background-color: white;
  height: 270px;
  border-radius: 4px;
`;
